import { CalendarOff, Pencil, Plus } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { ConfirmDialog } from "@/client/components/confirm-dialog";
import { Badge } from "@/client/components/ui/badge";
import { Button } from "@/client/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/client/components/ui/card";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/client/components/ui/dialog";
import { Input } from "@/client/components/ui/input";
import { Label } from "@/client/components/ui/label";
import { Skeleton } from "@/client/components/ui/skeleton";
import { useAuthStore } from "@/client/features/auth/auth-store";
import { ApiError } from "@/client/lib/api";
import { formatDateTime } from "@/client/lib/format";

import { type Hold, type HoldInput, holdsApi } from "./holds-api";

const textareaClass =
  "border-input bg-transparent focus-visible:border-ring focus-visible:ring-ring/50 min-h-20 w-full rounded-md border px-3 py-2 text-sm shadow-xs focus-visible:ring-[3px] focus-visible:outline-none";

/** ISO string -> value for <input type="datetime-local"> in local time. */
function toLocalInput(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

interface HoldFormState {
  title: string;
  description: string;
  affectedScope: string;
  startsAt: string;
  endsAt: string;
}

function emptyForm(): HoldFormState {
  return { title: "", description: "", affectedScope: "", startsAt: "", endsAt: "" };
}

function formFromHold(h: Hold): HoldFormState {
  return {
    title: h.title,
    description: h.description,
    affectedScope: h.affectedScope,
    startsAt: toLocalInput(h.startsAt),
    endsAt: toLocalInput(h.endsAt),
  };
}

export function HoldDialog({
  hold,
  onSaved,
  trigger,
  initialValues,
}: {
  hold?: Hold;
  onSaved: () => void;
  trigger: React.ReactNode;
  /** Prefill for a new hold (ignored when editing), e.g. scope from selected nodes. */
  initialValues?: Partial<HoldFormState>;
}) {
  const newForm = () => ({ ...emptyForm(), ...initialValues });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<HoldFormState>(hold ? formFromHold(hold) : newForm());
  const [saving, setSaving] = useState(false);
  // Holds are a self-service write, so a deactivated tenant can't create/edit
  // them (the server 403s); gate the submit as the authoritative guard for
  // every trigger that opens this dialog (PE-991).
  const deactivated = useAuthStore((s) => s.deactivated);

  // Reset to the source-of-truth values whenever the dialog (re)opens.
  const onOpenChange = (next: boolean) => {
    setOpen(next);
    if (next) {
      setForm(hold ? formFromHold(hold) : newForm());
    }
  };

  const update = <K extends keyof HoldFormState>(key: K, value: HoldFormState[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  const onSubmit = async () => {
    if (!form.title.trim()) {
      toast.error("Give the hold a title.");
      return;
    }
    if (!form.startsAt || !form.endsAt) {
      toast.error("Set a start and end time.");
      return;
    }
    const startsAt = new Date(form.startsAt);
    const endsAt = new Date(form.endsAt);
    if (endsAt <= startsAt) {
      toast.error("End time must be after the start time.");
      return;
    }

    const payload: HoldInput = {
      title: form.title.trim(),
      description: form.description.trim(),
      affectedScope: form.affectedScope.trim(),
      startsAt: startsAt.toISOString(),
      endsAt: endsAt.toISOString(),
    };

    setSaving(true);
    try {
      if (hold) {
        await holdsApi.update(hold.id, payload);
        toast.success("Hold updated.");
      } else {
        await holdsApi.create(payload);
        toast.success("Hold requested. We'll avoid disrupting your work during this window.");
      }
      setOpen(false);
      onSaved();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't save the hold.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{hold ? "Edit hold" : "Request a no-disruption hold"}</DialogTitle>
          <DialogDescription>
            Tell us a window when we should avoid disrupting your work. We'll factor it into our
            maintenance planning.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4">
          <div className="grid gap-2">
            <Label htmlFor="hold-title">Title</Label>
            <Input
              id="hold-title"
              value={form.title}
              maxLength={200}
              placeholder="Quarter-end batch run"
              onChange={(e) => update("title", e.target.value)}
            />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="grid gap-2">
              <Label htmlFor="hold-start">Starts</Label>
              <Input
                id="hold-start"
                type="datetime-local"
                value={form.startsAt}
                onChange={(e) => update("startsAt", e.target.value)}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="hold-end">Ends</Label>
              <Input
                id="hold-end"
                type="datetime-local"
                value={form.endsAt}
                onChange={(e) => update("endsAt", e.target.value)}
              />
            </div>
          </div>
          <div className="grid gap-2">
            <Label htmlFor="hold-scope">Affected nodes / scope (optional)</Label>
            <Input
              id="hold-scope"
              value={form.affectedScope}
              maxLength={2000}
              placeholder="e.g. gpu-pool-a, or all nodes"
              onChange={(e) => update("affectedScope", e.target.value)}
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="hold-desc">Notes (optional)</Label>
            <textarea
              id="hold-desc"
              className={textareaClass}
              value={form.description}
              maxLength={5000}
              placeholder="Anything we should know about this window."
              onChange={(e) => update("description", e.target.value)}
            />
          </div>
        </div>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline">Cancel</Button>
          </DialogClose>
          <Button onClick={onSubmit} disabled={saving || deactivated}>
            {saving ? "Saving…" : hold ? "Save changes" : "Request hold"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CancelHoldDialog({ hold, onCancelled }: { hold: Hold; onCancelled: () => void }) {
  const deactivated = useAuthStore((s) => s.deactivated);
  return (
    <ConfirmDialog
      trigger={
        <Button variant="ghost" size="sm" disabled={deactivated}>
          Cancel
        </Button>
      }
      title="Cancel this hold?"
      description={
        <>"{hold.title}" will be withdrawn. We'll no longer treat this window as protected.</>
      }
      confirmLabel="Cancel hold"
      pendingLabel="Cancelling…"
      cancelLabel="Keep hold"
      errorMessage="Couldn't cancel the hold."
      onConfirm={async () => {
        await holdsApi.cancel(hold.id);
        toast.success("Hold cancelled.");
        onCancelled();
      }}
    />
  );
}

export function HoldsCard({
  holds,
  error,
  onChanged,
}: {
  holds: Hold[] | null;
  error: boolean;
  onChanged: () => void;
}) {
  const active = holds?.filter((h) => h.status === "active") ?? [];
  const deactivated = useAuthStore((s) => s.deactivated);

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-2">
        <div className="space-y-1.5">
          <CardTitle className="flex items-center gap-2 text-base">
            <CalendarOff className="text-muted-foreground size-4" />
            Your no-disruption holds
          </CardTitle>
          <CardDescription>
            Windows you've asked us not to disrupt. We factor these into maintenance planning.
          </CardDescription>
        </div>
        <HoldDialog
          onSaved={onChanged}
          trigger={
            <Button size="sm" className="shrink-0" disabled={deactivated}>
              <Plus className="size-3.5" />
              Request hold
            </Button>
          }
        />
      </CardHeader>
      <CardContent>
        {/* Prefer showing whatever list we have: a failed refresh (e.g. after a
            successful create/cancel) shouldn't wipe a roster we already loaded.
            The toast surfaces the refresh failure. */}
        {holds === null ? (
          error ? (
            <p className="text-muted-foreground text-sm">
              Couldn't load your holds. Refresh to try again.
            </p>
          ) : (
            <div className="flex flex-col gap-2">
              <Skeleton className="h-8 w-full" />
              <Skeleton className="h-8 w-full" />
            </div>
          )
        ) : active.length === 0 ? (
          <p className="text-muted-foreground text-sm">
            No active holds. Request one to protect a window from maintenance.
          </p>
        ) : (
          <ul className="flex flex-col divide-y">
            {active.map((h) => (
              <li key={h.id} className="flex items-start justify-between gap-3 py-3 first:pt-0">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="truncate text-sm font-medium">{h.title}</p>
                    <Badge variant="secondary">Hold</Badge>
                  </div>
                  <p className="text-muted-foreground mt-0.5 text-xs">
                    {formatDateTime(h.startsAt)} – {formatDateTime(h.endsAt)}
                  </p>
                  {h.affectedScope ? (
                    <p className="text-muted-foreground mt-1 text-xs">Scope: {h.affectedScope}</p>
                  ) : null}
                  {h.description ? (
                    <p className="text-muted-foreground mt-1 text-xs">{h.description}</p>
                  ) : null}
                </div>
                <div className="flex shrink-0 items-center gap-1">
                  <HoldDialog
                    hold={h}
                    onSaved={onChanged}
                    trigger={
                      <Button variant="ghost" size="sm" disabled={deactivated}>
                        <Pencil className="size-3.5" />
                        Edit
                      </Button>
                    }
                  />
                  <CancelHoldDialog hold={h} onCancelled={onChanged} />
                </div>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
